import { useState } from "react";
import { supabase } from "@/lib/supabaseClient";

/* ─── Types ──────────────────────────────────────────────────── */
interface Props {
  tenantId: string;
  primary: string;
}

type FormState = {
  name:       string;
  email:      string;
  phone:      string;
  event_type: string;
  event_date: string;
  message:    string;
};

const EMPTY: FormState = {
  name:       "",
  email:      "",
  phone:      "",
  event_type: "",
  event_date: "",
  message:    "",
};

const EVENT_TYPES = [
  "Wedding",
  "Engagement Session",
  "Portrait Session",
  "Family Photos",
  "Commercial Shoot",
  "Corporate Event",
  "Other",
];

/* ─── Input class helper ─────────────────────────────────────── */
const INPUT =
  "w-full bg-[#fafafa] border border-black/8 rounded-lg px-3 py-2.5 text-sm text-[#1A1A2E] placeholder-[#1A1A2E]/20 outline-none transition-colors focus:border-[#C8A96E]/50";

/* ─── Component ──────────────────────────────────────────────── */
export default function ContactForm({ tenantId, primary }: Props) {
  const [form, setForm]       = useState<FormState>(EMPTY);
  const [errors, setErrors]   = useState<Partial<FormState>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);
  const [serverError, setServerError] = useState("");

  const set = (key: keyof FormState, val: string) =>
    setForm((p) => ({ ...p, [key]: val }));

  /* ── Validate ── */
  const validate = (): boolean => {
    const e: Partial<FormState> = {};
    if (!form.name.trim())  e.name  = "Name is required";
    if (!form.email.trim() && !form.phone.trim())
      e.email = "Please enter at least an email or phone";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Enter a valid email address";
    if (form.message.trim().length > 500)
      e.message = "Message must be under 500 characters";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* ── Submit ── */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setServerError("");

    const { data: insertedLead, error } = await supabase.from("tenant_public_leads").insert({
      tenant_id:  tenantId,
      name:       form.name.trim(),
      email:      form.email.trim() || null,
      phone:      form.phone.trim() || null,
      event_type: form.event_type  || null,
      event_date: form.event_date  || null,
      message:    form.message.trim() || null,
      status:     "new",
      source:     "public_website",
    }).select("id").maybeSingle();

    if (!error && insertedLead) {
      // Notify all admin users of this tenant about the new lead
      const { data: adminUsers } = await supabase
        .from("saas_tenant_users")
        .select("user_id")
        .eq("tenant_id", tenantId);

      if (adminUsers && adminUsers.length > 0) {
        const leadId = (insertedLead as { id: string }).id;
        await supabase.from("notifications").insert(
          adminUsers.map((u: { user_id: string }) => ({
            tenant_id: tenantId,
            user_id: u.user_id,
            type: "info",
            category: "lead",
            title: "New lead received",
            message: `${form.name.trim()} submitted a contact form${
              form.event_type ? ` for a ${form.event_type}` : ""
            }${
              form.event_date ? ` on ${new Date(form.event_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}` : ""
            }.`,
            entity_type: "lead",
            entity_id: leadId,
          }))
        );
      }
    }

    setSubmitting(false);

    if (error) {
      setServerError("Something went wrong. Please try again or contact us directly.");
      return;
    }

    setSubmitted(true);
    setForm(EMPTY);
  };

  /* ── Success state ── */
  if (submitted) {
    return (
      <div className="bg-white rounded-2xl border border-black/5 p-8 flex flex-col items-center text-center gap-4">
        <div
          className="w-14 h-14 flex items-center justify-center rounded-2xl"
          style={{ backgroundColor: primary + "15" }}
        >
          <i className="ri-check-double-line text-2xl" style={{ color: primary }}></i>
        </div>
        <div>
          <h3 className="text-[#1A1A2E] font-semibold text-base mb-1.5">
            Message received!
          </h3>
          <p className="text-[#1A1A2E]/50 text-sm leading-relaxed">
            Thanks for reaching out. We&apos;ll review your request and get back to you
            as soon as possible.
          </p>
        </div>
        <button
          onClick={() => setSubmitted(false)}
          className="text-xs text-[#1A1A2E]/30 hover:text-[#1A1A2E]/60 transition-colors cursor-pointer"
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white rounded-2xl border border-black/5 p-6 md:p-8 space-y-4"
    >
      <h3 className="text-[#1A1A2E] font-semibold text-sm">Send a Message</h3>

      {/* Name */}
      <div>
        <label className="text-[#1A1A2E]/40 text-xs mb-1.5 block">
          Your Name <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          value={form.name}
          onChange={(e) => set("name", e.target.value)}
          placeholder="Jane Doe"
          maxLength={80}
          className={`${INPUT} ${errors.name ? "border-red-300" : ""}`}
        />
        {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
      </div>

      {/* Email + Phone */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[#1A1A2E]/40 text-xs mb-1.5 block">Email</label>
          <input
            type="email"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            placeholder="jane@example.com"
            className={`${INPUT} ${errors.email ? "border-red-300" : ""}`}
          />
          {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
        </div>
        <div>
          <label className="text-[#1A1A2E]/40 text-xs mb-1.5 block">Phone</label>
          <input
            type="tel"
            value={form.phone}
            onChange={(e) => set("phone", e.target.value)}
            placeholder="+1 555 000 0000"
            className={INPUT}
          />
        </div>
      </div>

      {/* Event type + date */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[#1A1A2E]/40 text-xs mb-1.5 block">Event Type</label>
          <select
            value={form.event_type}
            onChange={(e) => set("event_type", e.target.value)}
            className={`${INPUT} cursor-pointer`}
          >
            <option value="">Select a type…</option>
            {EVENT_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[#1A1A2E]/40 text-xs mb-1.5 block">Event Date</label>
          <input
            type="date"
            value={form.event_date}
            onChange={(e) => set("event_date", e.target.value)}
            min={new Date().toISOString().split("T")[0]}
            className={INPUT}
          />
        </div>
      </div>

      {/* Message */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-[#1A1A2E]/40 text-xs">Message</label>
          <span className={`text-[10px] ${form.message.length > 450 ? "text-amber-500" : "text-[#1A1A2E]/20"}`}>
            {form.message.length}/500
          </span>
        </div>
        <textarea
          rows={4}
          value={form.message}
          onChange={(e) => set("message", e.target.value)}
          maxLength={500}
          placeholder="Tell us about your vision, the venue, number of guests…"
          className={`${INPUT} resize-none ${errors.message ? "border-red-300" : ""}`}
        />
        {errors.message && <p className="text-red-400 text-xs mt-1">{errors.message}</p>}
      </div>

      {/* Server error */}
      {serverError && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
          <span className="w-4 h-4 flex items-center justify-center text-red-500 shrink-0">
            <i className="ri-error-warning-line text-sm"></i>
          </span>
          <p className="text-red-600 text-xs">{serverError}</p>
        </div>
      )}

      {/* Submit */}
      <button
        type="submit"
        disabled={submitting}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-semibold text-white transition-all cursor-pointer whitespace-nowrap disabled:opacity-70"
        style={{ backgroundColor: primary }}
      >
        {submitting ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Sending…
          </>
        ) : (
          <>
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-send-plane-line"></i>
            </span>
            Send Message
          </>
        )}
      </button>

      <p className="text-[#1A1A2E]/20 text-[10px] text-center">
        We typically reply within 24 hours.
      </p>
    </form>
  );
}